/**
 * 安全表达式：只支持字面量、字段引用、四则/比较/逻辑、白名单函数。
 * 不使用 eval / Function；编译为 JS 片段供 P1 legacy parser / P2 生成脚本使用。
 */

export class ExpressionError extends Error {
  readonly position: number;
  constructor(message: string, position = -1) {
    super(position >= 0 ? `${message}（位置 ${position + 1}）` : message);
    this.name = 'ExpressionError';
    this.position = position;
  }
}

export type ExprNode =
  | { kind: 'num'; value: number }
  | { kind: 'str'; value: string }
  | { kind: 'bool'; value: boolean }
  | { kind: 'null' }
  | { kind: 'ident'; name: string }
  | { kind: 'member'; object: ExprNode; prop: string }
  | { kind: 'unary'; op: '!' | '-' | '+'; arg: ExprNode }
  | { kind: 'binary'; op: string; left: ExprNode; right: ExprNode }
  | { kind: 'call'; name: string; args: ExprNode[] };

export interface CompileExprOptions {
  /** 字段名 → JS 代码；返回 undefined 表示未知字段 */
  ident: (name: string) => string | undefined;
  /** 特殊变量映射，如 { $meta: 'meta', $index: 'i' } */
  meta?: Record<string, string>;
}

interface Token {
  type: 'num' | 'str' | 'id' | 'op' | 'eof';
  value: string;
  pos: number;
}

const FUNCTION_ARITY: Record<string, [number, number]> = {
  abs: [1, 1],
  round: [1, 2],
  floor: [1, 1],
  ceil: [1, 1],
  min: [1, 8],
  max: [1, 8],
  avg: [1, 8],
  sum: [1, 8],
  if: [3, 3],
  coalesce: [1, 8],
  number: [1, 1],
  string: [1, 1],
  bool: [1, 1],
  len: [1, 1],
  upper: [1, 1],
  lower: [1, 1],
  substr: [2, 3],
  replace: [3, 3],
  trim: [1, 1],
  cToF: [1, 1],
  fToC: [1, 1],
  mmToM: [1, 1],
  kPaToBar: [1, 1],
  pow: [2, 2],
  sqrt: [1, 1],
  now: [0, 0],
  date: [1, 1],
  formatDate: [1, 2],
};

const FUNCTION_JS: Record<string, (args: string[]) => string> = {
  abs: ([x]) => `Math.abs(${x})`,
  round: ([x, d]) => `_round(${x}, ${d ?? '0'})`,
  floor: ([x]) => `Math.floor(${x})`,
  ceil: ([x]) => `Math.ceil(${x})`,
  min: (args) => `Math.min(${args.join(', ')})`,
  max: (args) => `Math.max(${args.join(', ')})`,
  avg: (args) => `_avg([${args.join(', ')}])`,
  sum: (args) => `_sum([${args.join(', ')}])`,
  if: ([c, a, b]) => `(${c} ? ${a} : ${b})`,
  coalesce: (args) => `_coalesce([${args.join(', ')}])`,
  number: ([x]) => `_num(${x})`,
  string: ([x]) => `String(${x})`,
  bool: ([x]) => `_bool(${x})`,
  len: ([x]) => `String(${x}).length`,
  upper: ([x]) => `String(${x}).toUpperCase()`,
  lower: ([x]) => `String(${x}).toLowerCase()`,
  substr: ([s, a, b]) => (b ? `String(${s}).substr(${a}, ${b})` : `String(${s}).substr(${a})`),
  replace: ([s, a, b]) => `String(${s}).replace(${a}, ${b})`,
  trim: ([x]) => `String(${x}).trim()`,
  cToF: ([x]) => `_cToF(${x})`,
  fToC: ([x]) => `_fToC(${x})`,
  mmToM: ([x]) => `_scale(${x}, 0.001, 0)`,
  kPaToBar: ([x]) => `_scale(${x}, 0.01, 0)`,
  pow: ([a, b]) => `Math.pow(${a}, ${b})`,
  sqrt: ([x]) => `Math.sqrt(${x})`,
  now: () => 'Date.now()',
  date: ([x]) => `_date(${x})`,
  formatDate: ([x, f]) => (f ? `_fmtDate(${x}, ${f})` : `_fmtDate(${x})`),
};

/** 生成脚本/表达式里引用的运行时助手（无外部依赖） */
export const HELPER_PRELUDE = [
  'function _num(v) { if (v === null || v === undefined || v === \'\') return undefined; var n = Number(v); return isNaN(n) ? undefined : n; }',
  'function _bool(v) { if (typeof v === \'boolean\') return v; if (typeof v === \'number\') return v !== 0; var s = String(v).toLowerCase(); return s === \'true\' || s === \'1\' || s === \'on\' || s === \'yes\'; }',
  'function _round(v, d) { var n = _num(v); if (n === undefined) return undefined; var p = Math.pow(10, d || 0); return Math.round(n * p) / p; }',
  'function _sum(a) { var s = 0; for (var i = 0; i < a.length; i++) { var n = _num(a[i]); if (n !== undefined) s += n; } return s; }',
  'function _avg(a) { var n = 0, s = 0; for (var i = 0; i < a.length; i++) { var v = _num(a[i]); if (v !== undefined) { n++; s += v; } } return n ? s / n : undefined; }',
  'function _coalesce(a) { for (var i = 0; i < a.length; i++) { if (a[i] !== null && a[i] !== undefined && a[i] !== \'\') return a[i]; } return undefined; }',
  'function _cToF(v) { var n = _num(v); return n === undefined ? undefined : n * 9 / 5 + 32; }',
  'function _fToC(v) { var n = _num(v); return n === undefined ? undefined : (n - 32) * 5 / 9; }',
  'function _scale(v, s, o) { var n = _num(v); return n === undefined ? undefined : n * (s === undefined ? 1 : s) + (o || 0); }',
  'function _date(v) { var d = new Date(v); return isNaN(d.getTime()) ? undefined : d.toISOString(); }',
  'function _pad(n) { return (n < 10 ? \'0\' : \'\') + n; }',
  'function _fmtDate(v, f) { var d = new Date(v); if (isNaN(d.getTime())) return undefined; var s = f || \'YYYY-MM-DD HH:mm:ss\'; return s.replace(\'YYYY\', String(d.getFullYear())).replace(\'MM\', _pad(d.getMonth() + 1)).replace(\'DD\', _pad(d.getDate())).replace(\'HH\', _pad(d.getHours())).replace(\'mm\', _pad(d.getMinutes())).replace(\'ss\', _pad(d.getSeconds())); }',
  'function _get(o, p) { if (p === undefined || p === null || p === \'\') return o; var segs = String(p).replace(/\\[(\\d+)\\]/g, \'.$1\').split(\'.\').filter(function (s) { return s !== \'\'; }); var cur = o; for (var i = 0; i < segs.length; i++) { if (cur === null || cur === undefined) return undefined; cur = cur[segs[i]]; } return cur; }',
].join('\n');

function tokenize(src: string): Token[] {
  const out: Token[] = [];
  let i = 0;
  while (i < src.length) {
    const ch = src[i];
    if (/\s/.test(ch)) {
      i++;
      continue;
    }
    if (/[0-9]/.test(ch) || (ch === '.' && /[0-9]/.test(src[i + 1] || ''))) {
      let j = i;
      let dot = false;
      while (j < src.length && (/[0-9]/.test(src[j]) || (src[j] === '.' && !dot))) {
        if (src[j] === '.') dot = true;
        j++;
      }
      out.push({ type: 'num', value: src.slice(i, j), pos: i });
      i = j;
      continue;
    }
    if (ch === '"' || ch === "'") {
      const quote = ch;
      let j = i + 1;
      let value = '';
      while (j < src.length && src[j] !== quote) {
        if (src[j] === '\\') {
          const n = src[j + 1];
          const map: Record<string, string> = { n: '\n', r: '\r', t: '\t', '\\': '\\', '"': '"', "'": "'" };
          value += map[n] ?? n;
          j += 2;
        } else {
          value += src[j];
          j++;
        }
      }
      if (j >= src.length) throw new ExpressionError('字符串未闭合', i);
      out.push({ type: 'str', value, pos: i });
      i = j + 1;
      continue;
    }
    if (/[A-Za-z_$]/.test(ch)) {
      let j = i;
      while (j < src.length && /[A-Za-z0-9_$]/.test(src[j])) j++;
      out.push({ type: 'id', value: src.slice(i, j), pos: i });
      i = j;
      continue;
    }
    const three = src.slice(i, i + 3);
    if (three === '===' || three === '!==') {
      out.push({ type: 'op', value: three, pos: i });
      i += 3;
      continue;
    }
    const two = src.slice(i, i + 2);
    if (['==', '!=', '>=', '<=', '&&', '||'].includes(two)) {
      out.push({ type: 'op', value: two, pos: i });
      i += 2;
      continue;
    }
    if ('+-*/%!><(),.'.includes(ch)) {
      out.push({ type: 'op', value: ch, pos: i });
      i++;
      continue;
    }
    throw new ExpressionError(`无法识别的字符「${ch}」`, i);
  }
  out.push({ type: 'eof', value: '', pos: src.length });
  return out;
}

class Parser {
  private idx = 0;
  constructor(private readonly toks: Token[]) {}

  private peek(): Token {
    return this.toks[this.idx];
  }

  private next(): Token {
    return this.toks[this.idx++];
  }

  private matchOp(op: string): boolean {
    const t = this.peek();
    if (t.type === 'op' && t.value === op) {
      this.idx++;
      return true;
    }
    return false;
  }

  private expectOp(op: string): void {
    const t = this.next();
    if (t.type !== 'op' || t.value !== op) throw new ExpressionError(`缺少「${op}」`, t.pos);
  }

  parse(): ExprNode {
    const e = this.parseOr();
    const t = this.peek();
    if (t.type !== 'eof') throw new ExpressionError(`多余的字符「${t.value}」`, t.pos);
    return e;
  }

  private parseOr(): ExprNode {
    let left = this.parseAnd();
    while (this.matchOp('||')) left = { kind: 'binary', op: '||', left, right: this.parseAnd() };
    return left;
  }

  private parseAnd(): ExprNode {
    let left = this.parseEquality();
    while (this.matchOp('&&')) left = { kind: 'binary', op: '&&', left, right: this.parseEquality() };
    return left;
  }

  private parseEquality(): ExprNode {
    let left = this.parseComparison();
    for (;;) {
      const t = this.peek();
      if (t.type === 'op' && ['==', '!=', '===', '!=='].includes(t.value)) {
        this.next();
        left = { kind: 'binary', op: t.value, left, right: this.parseComparison() };
      } else {
        return left;
      }
    }
  }

  private parseComparison(): ExprNode {
    let left = this.parseAdditive();
    for (;;) {
      const t = this.peek();
      if (t.type === 'op' && ['>', '>=', '<', '<='].includes(t.value)) {
        this.next();
        left = { kind: 'binary', op: t.value, left, right: this.parseAdditive() };
      } else {
        return left;
      }
    }
  }

  private parseAdditive(): ExprNode {
    let left = this.parseMultiplicative();
    for (;;) {
      const t = this.peek();
      if (t.type === 'op' && (t.value === '+' || t.value === '-')) {
        this.next();
        left = { kind: 'binary', op: t.value, left, right: this.parseMultiplicative() };
      } else {
        return left;
      }
    }
  }

  private parseMultiplicative(): ExprNode {
    let left = this.parseUnary();
    for (;;) {
      const t = this.peek();
      if (t.type === 'op' && (t.value === '*' || t.value === '/' || t.value === '%')) {
        this.next();
        left = { kind: 'binary', op: t.value, left, right: this.parseUnary() };
      } else {
        return left;
      }
    }
  }

  private parseUnary(): ExprNode {
    const t = this.peek();
    if (t.type === 'op' && (t.value === '!' || t.value === '-' || t.value === '+')) {
      this.next();
      return { kind: 'unary', op: t.value as '!' | '-' | '+', arg: this.parseUnary() };
    }
    return this.parsePostfix();
  }

  private parsePostfix(): ExprNode {
    let node = this.parsePrimary();
    while (this.matchOp('.')) {
      const t = this.next();
      if (t.type !== 'id') throw new ExpressionError('「.」后需为字段名', t.pos);
      node = { kind: 'member', object: node, prop: t.value };
    }
    return node;
  }

  private parsePrimary(): ExprNode {
    const t = this.next();
    if (t.type === 'num') return { kind: 'num', value: Number(t.value) };
    if (t.type === 'str') return { kind: 'str', value: t.value };
    if (t.type === 'id') {
      if (t.value === 'true') return { kind: 'bool', value: true };
      if (t.value === 'false') return { kind: 'bool', value: false };
      if (t.value === 'null') return { kind: 'null' };
      if (this.matchOp('(')) {
        const args: ExprNode[] = [];
        if (!this.matchOp(')')) {
          do {
            args.push(this.parseOr());
          } while (this.matchOp(','));
          this.expectOp(')');
        }
        return { kind: 'call', name: t.value, args };
      }
      return { kind: 'ident', name: t.value };
    }
    if (t.type === 'op' && t.value === '(') {
      const e = this.parseOr();
      this.expectOp(')');
      return e;
    }
    throw new ExpressionError(`意外的符号「${t.value || '结束'}」`, t.pos);
  }
}

export function parseExpression(src: string): ExprNode {
  const text = String(src ?? '').trim();
  if (!text) throw new ExpressionError('表达式不能为空');
  if (text.length > 500) throw new ExpressionError('表达式过长（≤500）');
  return new Parser(tokenize(text)).parse();
}

export function collectIdentifiers(node: ExprNode, out = new Set<string>()): Set<string> {
  switch (node.kind) {
    case 'ident':
      out.add(node.name);
      break;
    case 'member':
      collectIdentifiers(node.object, out);
      break;
    case 'unary':
      collectIdentifiers(node.arg, out);
      break;
    case 'binary':
      collectIdentifiers(node.left, out);
      collectIdentifiers(node.right, out);
      break;
    case 'call':
      for (const a of node.args) collectIdentifiers(a, out);
      break;
    default:
      break;
  }
  return out;
}

export function collectFunctionCalls(node: ExprNode, out = new Set<string>()): Set<string> {
  switch (node.kind) {
    case 'call':
      out.add(node.name);
      for (const a of node.args) collectFunctionCalls(a, out);
      break;
    case 'member':
      collectFunctionCalls(node.object, out);
      break;
    case 'unary':
      collectFunctionCalls(node.arg, out);
      break;
    case 'binary':
      collectFunctionCalls(node.left, out);
      collectFunctionCalls(node.right, out);
      break;
    default:
      break;
  }
  return out;
}

export function compileExpression(node: ExprNode, opts: CompileExprOptions): string {
  switch (node.kind) {
    case 'num':
      return String(node.value);
    case 'str':
      return JSON.stringify(node.value);
    case 'bool':
      return node.value ? 'true' : 'false';
    case 'null':
      return 'null';
    case 'ident': {
      const meta = opts.meta?.[node.name];
      if (meta !== undefined) return meta;
      const code = opts.ident(node.name);
      if (code === undefined) throw new ExpressionError(`未知字段「${node.name}」`);
      return code;
    }
    case 'member':
      return `(${compileExpression(node.object, opts)}).${node.prop}`;
    case 'unary':
      return `(${node.op}${compileExpression(node.arg, opts)})`;
    case 'binary':
      return `(${compileExpression(node.left, opts)} ${node.op} ${compileExpression(node.right, opts)})`;
    case 'call': {
      const arity = FUNCTION_ARITY[node.name];
      if (!arity) throw new ExpressionError(`未知函数「${node.name}」`);
      if (node.args.length < arity[0] || node.args.length > arity[1]) {
        throw new ExpressionError(`函数「${node.name}」需要 ${arity[0]}${arity[0] === arity[1] ? '' : `~${arity[1]}`} 个参数`);
      }
      const fn = FUNCTION_JS[node.name];
      if (!fn) throw new ExpressionError(`函数「${node.name}」暂不支持`);
      return fn(node.args.map((a) => compileExpression(a, opts)));
    }
    default:
      throw new ExpressionError('未知表达式');
  }
}
